import 'reflect-metadata';
import { Container } from 'typedi';
import { ClientInitToken } from './injection-tokens';
import {
    setLoaded,
    sendCorrelationEvent,
    addMaintenance,
    setDocumentEventsListeners,
    setEntryLoaded,
    setLastProductCookie,
} from './modules/client-integration/init';
import { ClientProvider, CloseSessionReason } from './models/enums/general-enums';
import { MessageBroker } from '@sparkware/uc-sdk-core';
import { PerformanceManager } from './modules/performance/performance-manager';
import { exportFeatureChecks } from './modules/feature';
import { getAllowAttr } from '@888webteam/iframe-allow-camera';
import { initMessageBroker } from './modules/client-integration/init/registrations/message-broker';
import { pushDataLayerData } from './modules/client-integration/init/registrations/data-layer';
import {
    initWindowAndStaticClass,
    setMutualServices,
} from './modules/client-integration/init/registrations/services/services';
import { setClientServices } from './modules/client-integration/init/registrations/services/client-services';
import { getClientInitObject } from './modules/client-integration/init/registrations/client-init';
import { EnvAccessAuthentication } from './modules/environment-access-auth';
import { B2CIntegration } from './modules/b2c-integration';
import { ContentAdapter } from './modules/b2c-integration/content-adapter';
import { NativeUtils } from './modules/native/native.utils';
import { LoaderManager } from './loaders/LoaderManager';
import { Utils } from './modules/utils';
import { KambiClientInit } from './modules/client-integration/init/kambi-client-init';
import { PokerClientInit } from './modules/client-integration/init/poker-client-init';
import { SpectateClientInit } from './modules/client-integration/init/spectate-client-init';
import { ApiManager } from '../Modules/Utils/API/ApiManager';
import { LoggerProvider } from './modules/logger';
import { AttributionStore } from './modules/attribution/storage/attribution-store';

initWindowAndStaticClass();
const channels = initMessageBroker('Init');
setMutualServices();
setClientServices();
addMaintenance();
const clientInit = getClientInit();
void initialize();

async function initialize(): Promise<void> {
    const initObject = await getClientInitObject();
    const exportedObject = createExportObject(initObject);
    pushDataLayerData();
    setDocumentEventsListeners();
    addExportObjectsToGlobal(exportedObject);
    setEntryLoaded(exportedObject);
    setLoaded(exportedObject);
    sendCorrelationEvent();
    setLastProductCookie();
    void addNativeServiceToGlobal();
}

function createExportObject(initObject: Awaited<ReturnType<typeof getClientInitObject>>) {
    return {
        CloseSessionReasonEnum: CloseSessionReason,
        getClientSettings: async () => await clientInit.getClientSettings(),
        channels,
        performanceManager: Container.get(PerformanceManager),
        allowAttrFunction: getAllowAttr,
        feature: exportFeatureChecks(),
        envAccessAuthFunctions: {
            checkPassword: Container.get(EnvAccessAuthentication).checkPassword,
        },
        clientInit: initObject,
        init: {
            onClientLoad: async (initFn: Function): Promise<void> => {
                await clientInit.onClientLoad();
                if (initFn) {
                    await initFn(initObject);
                }
            },
        },
        messagebroker: MessageBroker,
        getOptimizelyData: async () => {
            const optimizely = await LoaderManager.Instance.OptimizelyLoader.Instance;
            return optimizely ? optimizely.getOptimizelyData() : null;
        },
        b2c: Container.get(B2CIntegration),
        getBussinessCorrelationId: () => Container.get(AttributionStore).peek(),
    };
}

function addExportObjectsToGlobal(exportedObject: any) {
    const contentAdapter = Container.get(ContentAdapter);
    global.ucf = exportedObject;
    global.uc = exportedObject;
    global.contentAdapter = contentAdapter;
    global.ucSDK = {
        api: {
            instance: ApiManager.getAPIInstance(),
            nonServiceInstance: ApiManager.getNonServiceInstance(),
        },
        logger: Container.get(LoggerProvider),
    };
}

async function addNativeServiceToGlobal() {
    const nativeService = await LoaderManager.Instance.NativeServiceLoader.Instance;
    if (nativeService) {
        global.NativeInterface = nativeService;
        Object.keys(NativeUtils).map((key) => (window[key] = NativeUtils[key]));
    }
}

function getClientInit() {
    const utils = Container.get(Utils);
    const clientProvider = utils.getClientProvider();
    const clients = {
        [ClientProvider.Kambi]: () => Container.get(KambiClientInit),
        [ClientProvider.Poker]: () => Container.get(PokerClientInit),
        [ClientProvider.Spectate]: () => Container.get(SpectateClientInit),
    };
    const clientInit = clients[clientProvider]
        ? clients[clientProvider]()
        : clients[ClientProvider.Spectate]();
    Container.set(ClientInitToken, clientInit);
    return clientInit;
}
